<template>
  <div class="blissrose-gpu">
    <!-- 总览 -->
    <el-card shadow="never" class="summary-card">
      <div slot="header" class="card-header">
        <span>显卡总览</span>
        <div class="card-header-right">
          <span class="updated-at">{{ updatedAt ? `更新于 ${updatedAt}` : '尚未取到数据' }}</span>
          <el-button size="small" icon="el-icon-refresh" :loading="loading" @click="refresh">刷新</el-button>
        </div>
      </div>

      <div class="summary-grid">
        <div v-for="item in summaryItems" :key="item.label" class="summary-item">
          <div class="summary-value">{{ item.value }}</div>
          <div class="summary-label">{{ item.label }}</div>
        </div>
      </div>
    </el-card>

    <!-- 获取数据连续失败到阈值就停轮询 -->
    <el-alert v-if="pollPaused" type="error" show-icon :closable="false" class="notice">
      <template slot="title">已暂停自动刷新</template>
      <div>
        连续 {{ failTolerance }} 次获取数据失败，下面显示的数字可能已经过期。
      </div>
      <el-button size="mini" type="primary" class="notice-btn" @click="refresh">立即重试</el-button>
    </el-alert>

    <!-- 未读到显卡信息时给出排查方向 -->
    <el-alert v-else-if="!loading && !gpus.length" type="warning" show-icon :closable="false" class="notice">
      <template slot="title">未读取到显卡信息</template>
      <div>
        请确认容器启动时已挂载 GPU（CDI 参数 <span class="mono">--device nvidia.com/gpu=all</span>），
        且镜像内已安装 PyTorch。
      </div>
    </el-alert>

    <!-- 逐卡 -->
    <el-row :gutter="12">
      <el-col v-for="gpu in gpus" :key="gpu.index" :xs="24" :sm="12" :md="8" :lg="6" class="gpu-col">
        <el-card shadow="hover" class="gpu-card">
          <div class="gpu-head">
            <span class="gpu-index">GPU {{ gpu.index }}</span>
            <el-tag :type="loadTag(gpu)" size="mini">{{ pct(gpu, 'utilization_gpu') }}%</el-tag>
          </div>
          <div class="gpu-name" :title="gpu.name">{{ gpu.name || '未知型号' }}</div>

          <div class="gpu-metric">
            <div class="metric-head">
              <span>显存</span>
              <span>{{ memText(gpu) }}</span>
            </div>
            <el-progress
              :percentage="memPercent(gpu)"
              :status="memStatus(gpu)"
              :show-text="false"
              :stroke-width="10"
            />
          </div>

          <div class="gpu-metric">
            <div class="metric-head">
              <span>利用率</span>
              <span>{{ pct(gpu, 'utilization_gpu') }}%</span>
            </div>
            <el-progress
              :percentage="pct(gpu, 'utilization_gpu')"
              :status="utilStatus(gpu)"
              :show-text="false"
              :stroke-width="10"
            />
          </div>

          <div class="gpu-foot">
            <span>温度 {{ tempText(gpu) }}</span>
            <span title="本服务通过 PyTorch 申请的显存（不是整卡占用）">
              PyTorch 已申请 {{ gb(gpu.torch_allocated_mb) }} GB
            </span>
          </div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import { fetchGpuStatus } from '../../api/blissrose/inference';

// 显卡状态轮询；获取失败到阈值就停
const POLL_INTERVAL_MS = 3000;
const FAIL_TOLERANCE = 3;

/** 显存占用率分级，用于进度条配色 */
const MEM_WARN = 70;
const MEM_DANGER = 90;
/** 利用率分级 */
const UTIL_WARN = 60;
const UTIL_DANGER = 90;

export default {
  name: 'GpuMonitor',

  data() {
    return {
      loading: false,
      gpus: [],
      updatedAt: '',
      failCount: 0,
      pollPaused: false,
      failTolerance: FAIL_TOLERANCE,
      pollTimer: null,
    };
  },

  computed: {
    /** 顶部四个汇总数字，取不到卡时全部显示占位符 */
    summaryItems() {
      if (!this.gpus.length) {
        return [
          { label: '显卡数', value: '0' },
          { label: '已用显存', value: '-' },
          { label: '平均利用率', value: '-' },
          { label: '最高温度', value: '-' },
        ];
      }
      const used = this.sum('memory_used_mb');
      const total = this.sum('memory_total_mb');
      const temps = this.gpus.map(g => this.num(g.temperature_c)).filter(t => t > 0);
      const avgUtil = Math.round(
        this.gpus.reduce((acc, g) => acc + this.num(g.utilization_gpu), 0) / this.gpus.length,
      );
      return [
        { label: '显卡数', value: String(this.gpus.length) },
        { label: '已用显存', value: `${this.gb(used)} / ${this.gb(total)} GB` },
        { label: '平均利用率', value: `${avgUtil}%` },
        { label: '最高温度', value: temps.length ? `${Math.max(...temps)}°C` : '-' },
      ];
    },
  },

  mounted() {
    this.loadGpus();
    this.startPoll();
  },

  beforeDestroy() {
    this.stopPoll();
  },

  methods: {
    /** 立即取一次，并清掉失败计数、恢复轮询 */
    refresh() {
      this.failCount = 0;
      this.pollPaused = false;
      this.startPoll();
      this.loadGpus();
    },

    /** 先清再设，避免重复挂定时器 */
    startPoll() {
      this.stopPoll();
      this.pollTimer = setInterval(() => {
        // 上一次没回来就跳过这一拍
        if (!this.loading) this.loadGpus(true);
      }, POLL_INTERVAL_MS);
    },

    stopPoll() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },

    /** 拉取显卡状态；silent 为定时轮询，不显示加载态 */
    async loadGpus(silent = false) {
      if (!silent) this.loading = true;
      try {
        const { gpus } = (await fetchGpuStatus()).data;
        this.gpus = (gpus || []).slice().sort((a, b) => a.index - b.index);
        this.updatedAt = dayjs().format('HH:mm:ss');
        this.failCount = 0;
      } catch (err) {
        // 错误提示由响应拦截器统一弹出；计数到阈值后停轮询
        this.failCount += 1;
        if (this.failCount >= FAIL_TOLERANCE) {
          this.pollPaused = true;
          this.stopPoll();
        }
      } finally {
        if (!silent) this.loading = false;
      }
    },

    num(value) {
      return Number(value) || 0;
    },

    gb(mb) {
      return (this.num(mb) / 1024).toFixed(1);
    },

    sum(field) {
      return this.gpus.reduce((acc, g) => acc + this.num(g[field]), 0);
    },

    pct(gpu, field) {
      return Math.min(100, Math.round(this.num(gpu[field])));
    },

    /** 显存占用百分比；总量缺失时按 0 处理，不显示 NaN */
    memPercent(gpu) {
      const total = this.num(gpu.memory_total_mb);
      if (!total) return 0;
      return Math.min(100, Math.round((this.num(gpu.memory_used_mb) / total) * 100));
    },

    memText(gpu) {
      const total = this.num(gpu.memory_total_mb);
      if (!total) return '-';
      return `${this.gb(gpu.memory_used_mb)} / ${this.gb(total)} GB (${this.memPercent(gpu)}%)`;
    },

    memStatus(gpu) {
      const value = this.memPercent(gpu);
      if (value >= MEM_DANGER) return 'exception';
      if (value >= MEM_WARN) return 'warning';
      return 'success';
    },

    utilStatus(gpu) {
      const value = this.pct(gpu, 'utilization_gpu');
      if (value >= UTIL_DANGER) return 'exception';
      if (value >= UTIL_WARN) return 'warning';
      return 'success';
    },

    /** 右上角标签跟随利用率配色 */
    loadTag(gpu) {
      const status = this.utilStatus(gpu);
      if (status === 'exception') return 'danger';
      if (status === 'warning') return 'warning';
      return 'info';
    },

    tempText(gpu) {
      const value = this.num(gpu.temperature_c);
      return value > 0 ? `${value}°C` : '-';
    },
  },
};
</script>

<style scoped>
.summary-card {
  margin-bottom: 12px;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.card-header-right {
  display: flex;
  align-items: center;
}

.updated-at {
  margin-right: 12px;
  font-size: 12px;
  color: #909399;
}

.summary-grid {
  display: flex;
  flex-wrap: wrap;
}

.summary-item {
  flex: 1 1 150px;
  padding: 4px 0;
}

.summary-value {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
}

.summary-label {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}

.notice {
  margin-bottom: 12px;
}

.notice-btn {
  margin-top: 8px;
}

.mono {
  padding: 0 4px;
  border-radius: 2px;
  background: #f5f7fa;
  font-family: Consolas, Monaco, monospace;
}

.gpu-col {
  margin-bottom: 12px;
}

.gpu-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.gpu-index {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.gpu-name {
  margin-top: 2px;
  overflow: hidden;
  font-size: 12px;
  color: #909399;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.gpu-metric {
  margin-top: 12px;
}

.metric-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
  font-size: 12px;
  color: #606266;
}

.gpu-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
  padding-top: 8px;
  border-top: 1px solid #ebeef5;
  font-size: 12px;
  color: #909399;
}
</style>
